import React, { useEffect, useRef, useState } from 'react';
import './Portfolio.css';
import ProjectsPage from './ProjectsPage.js';
import Documents from './Documents';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import { FiFileText, FiActivity, FiHome, FiMail, FiPhone, FiLinkedin, FiGithub, FiBriefcase, FiMapPin } from 'react-icons/fi';

// ─── Scroll Reveal ───────────────────────────────────────────────────────────
const ScrollReveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transitionDelay = `${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('revealed'); },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [delay]);
  return <div ref={ref} className={`sr-item ${className}`}>{children}</div>;
};

// ─── Scroll Float (char-by-char) ─────────────────────────────────────────────
const ScrollFloat = ({ children, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('sf-visible'); },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, []);
  const text = typeof children === 'string' ? children : '';
  return (
    <h2 ref={ref} className={`scroll-float ${className}`}>
      {text.split('').map((char, i) => (
        <span key={i} className="sf-char" style={{ '--i': i }}>
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </h2>
  );
};

// ─── Marquee Skills ───────────────────────────────────────────────────────────
const MarqueeTrack = ({ skills, reverse = false }) => (
  <div className={`marquee-track ${reverse ? 'marquee-reverse' : ''}`}>
    <div className="marquee-inner">
      {[...skills, ...skills].map((s, i) => (
        <div className="marquee-chip" key={i}>
          <img src={s.image} alt={s.name} />
          <span>{s.name}</span>
        </div>
      ))}
    </div>
  </div>
);

// ─── Spotify Player ───────────────────────────────────────────────────────────
const SpotifyPlayer = () => {
  const [open, setOpen] = useState(false);
  
  // Your playlist ID
  const playlistId = '42gcppn68L9rFDKfLN6Ce6';
  
  return (
    <div className={`spotify-float ${open ? 'spotify-open' : ''}`}>
      <button className="spotify-toggle" onClick={() => setOpen(o => !o)} title="Toggle music player">
        <span className="spotify-icon">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
        </span>
        <span className="spotify-label">{open ? 'Close' : 'Play Music'}</span>
      </button>
      {open && (
        <div className="spotify-embed">
          <iframe
            src={`https://open.spotify.com/embed/playlist/${playlistId}`}
            width="100%"
            height="352"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Spotify Playlist"
          />
        </div>
      )}
    </div>
  );
};
// ─── Experience Section ───────────────────────────────────────────────────────
const Experience = () => (
  <section className="experience-section">
    <ScrollReveal>
      <div className="section-header">
        <span className="section-number">03</span>
        <h2 className="section-title">Experience</h2>
        <div className="section-line" />
      </div>
    </ScrollReveal>

    <ScrollReveal delay={100}>
      <div className="exp-card">
        <div className="exp-left">
          <img src="images/scatec.png" alt="Scatec Africa" className="exp-logo" />
        </div>
        <div className="exp-right">
          <div className="exp-meta">
            <span className="exp-company">Scatec Africa Ltd</span>
            <span className="exp-period">Jan 2025 — Jan 2026</span>
          </div>
          <h3 className="exp-role">IT Technician · Burgersdorp iCentre</h3>
          <ul className="exp-points">
            <li>Developed a custom Ticketing System for Walter Sisulu Municipality to streamline IT support workflows.</li>
            <li>Gained hands-on experience in network troubleshooting — physical layer (RJ-45, Ethernet), core networking protocols.</li>
            <li>Provided first-line IT support: hardware, software, and network issue resolution for 100+ users.</li>
            <li>Automated data collection and reporting with custom scripts and APIs, cutting manual effort by 40%.</li>
            <li>Managed Linux system operations including setup, troubleshooting, and security monitoring.</li>
          </ul>
        </div>
      </div>
    </ScrollReveal>
  </section>
);

// ─── What I Do ────────────────────────────────────────────────────────────────
const WhatIDo = () => {
  const services = [
    {
      icon: '⬡',
      title: 'Web Development',
      items: ['Full-stack Spring Boot + React applications', 'REST APIs & third-party integrations', 'Performance-optimised SPAs'],
    },
    {
      icon: '◈',
      title: 'Cloud & DevOps',
      items: ['AWS EC2, S3, Huawei ECS deployments', 'Docker containerisation & CI/CD via GitHub Actions', 'PostgreSQL & MongoDB administration'],
    },
    {
      icon: '◎',
      title: 'AI Integration',
      items: ['Google Gemini API & LLM-powered features', 'Automated documentation generation', 'Intelligent data processing pipelines'],
    },
    {
      icon: '⬢',
      title: 'Cyber Security',
      items: ['Network troubleshooting & monitoring', 'JWT auth & role-based access control', 'Linux security management'],
    },
  ];
  return (
    <section className="services-section">
      <ScrollReveal>
        <div className="section-header">
          <span className="section-number">02</span>
          <h2 className="section-title">What I Do</h2>
          <div className="section-line" />
        </div>
      </ScrollReveal>
      <div className="services-grid">
        {services.map((s, i) => (
          <ScrollReveal key={i} delay={i * 80}>
            <div className="service-card">
              <span className="service-icon">{s.icon}</span>
              <h3>{s.title}</h3>
              <ul>{s.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>
  );
};

// ─── Home Page ────────────────────────────────────────────────────────────────
const Home = () => (
  <div className="content">
    {/* Hero */}
    <section className="hero-section">
      <div className="hero-noise" />
      <div className="hero-inner">
        <ScrollReveal>
          <p className="hero-eyebrow">
            <span className="eyebrow-dot" />
           Midrand, JHB
          </p>
        </ScrollReveal>

        <div className="hero-name-wrap">
          <ScrollFloat>Sinalo</ScrollFloat>
          <ScrollFloat>Ntlanganiso</ScrollFloat>
        </div>

        <ScrollReveal delay={200}>
          <p className="hero-role">IT Technician &amp; Full Stack Developer</p>
        </ScrollReveal>

        <ScrollReveal delay={300}>
          <p className="hero-bio">
            Final-year ICT Application Development student at TUT, graduating May 2026.
            I build scalable systems, intuitive interfaces, and explore
            the intersection of cloud, AI, and modern web tech.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={400}>
          <div className="hero-cta">
            <a href="https://github.com/SA-Ntlanganiso" target="_blank" rel="noopener noreferrer" className="btn-primary">
              GitHub ↗
            </a>
            <a href="mailto:asinalo87@gmail.com" className="btn-secondary">
              asinalo87@gmail.com
            </a>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={500}>
          <div className="hero-avatar-strip">
            <div className="avatar-ring">
              <img src="images/ali.jpeg" alt="Sinalo Ntlanganiso" />
            </div>
            <div className="hero-quick-links">
              <a href="https://linkedin.com/in/sinalo-alizwa-ntlanganiso-06a511320" target="_blank" rel="noopener noreferrer"><FiLinkedin /> LinkedIn</a>
              <a href="https://github.com/SA-Ntlanganiso" target="_blank" rel="noopener noreferrer"><FiGithub /> GitHub</a>
              <a href="tel:0730819763"><FiPhone /> 073 081 9763</a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>

    {/* Marquee Skills */}
    <section className="marquee-section">
      <ScrollReveal>
        <div className="section-header">
          <span className="section-number">01</span>
          <h2 className="section-title">Skills &amp; Stack</h2>
          <div className="section-line" />
        </div>
      </ScrollReveal>
      <MarqueeTrack skills={frontendBackendSkills} />
      <MarqueeTrack skills={[...distributionSkills, ...mobileSkills, ...deploymentTech]} reverse />
    </section>

    {/* What I Do */}
    <WhatIDo />

    {/* Experience */}
    <Experience />

    {/* About */}
    <section className="about-section">
      <ScrollReveal>
        <div className="section-header">
          <span className="section-number">04</span>
          <h2 className="section-title">About Me</h2>
          <div className="section-line" />
        </div>
      </ScrollReveal>

      <div className="about-grid">
        {[
          {
            label: 'Background',
            items: [
              'Grew up in Burgersdorp — saw firsthand how tech transforms communities.',
              'Passionate about solving real problems through software.',
              'Studying ICT App Dev at TUT, graduating May 2026.',
            ],
          },
          {
            label: 'Core Stack',
            items: [
              'Backend: Spring Boot, Java, Python, Node.js',
              'Frontend: React, TypeScript, HTML/CSS',
              'Cloud: AWS, Huawei ECS, Vercel, Supabase',
            ],
          },
          {
            label: 'Flagship Projects',
            items: [
              'DOZAI — AI documentation generation platform',
              'Walter Sisulu Municipality IT Help Desk System',
              'Split — Group Wallet powered by NetBank API',
            ],
          },
          {
            label: 'Career Goals',
            items: [
              'Build impactful software at scale.',
              'Grow in cloud, AI, and cybersecurity.',
              'Mentor developers & contribute open-source.',
            ],
          },
        ].map((card, i) => (
          <ScrollReveal key={i} delay={i * 60}>
            <div className="about-card">
              <p className="about-card-title">{card.label}</p>
              <ul>{card.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>

    <Footer />
  </div>
);

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="site-footer">
    <ScrollReveal>
      <p className="footer-eyebrow">Open to opportunities</p>
      <h3 className="footer-heading">Let's Connect</h3>
      <p className="footer-sub">Actively seeking graduate roles in Software Dev &amp; Cyber Security.</p>
    </ScrollReveal>

    <div className="footer-grid">
      {[
        { icon: <FiMail />, label: 'asinalo87@gmail.com', href: 'mailto:asinalo87@gmail.com' },
        { icon: <FiMail />, label: '222730619@tut4life.ac.za', href: 'mailto:222730619@tut4life.ac.za' },
        { icon: <FiPhone />, label: '073 081 9763', href: 'tel:0730819763' },
        { icon: <FiMapPin />, label: 'Midrand, Johannesburg', href: '#' },
        { icon: <FiLinkedin />, label: 'LinkedIn', href: 'https://linkedin.com/in/sinalo-alizwa-ntlanganiso-06a511320' },
        { icon: <FiGithub />, label: 'GitHub', href: 'https://github.com/SA-Ntlanganiso' },
      ].map((c, i) => (
        <ScrollReveal key={i} delay={i * 50}>
          <a href={c.href} className="footer-link" target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
            {c.icon}<span>{c.label}</span>
          </a>
        </ScrollReveal>
      ))}
    </div>

    <ScrollReveal delay={300}>
      <div className="footer-availability">
        <span>Available for</span>
        {['Full-time', 'Internship', 'Contract', 'Open Source'].map(t => (
          <span key={t} className="avail-tag">{t}</span>
        ))}
      </div>
    </ScrollReveal>

    <ScrollReveal delay={400}>
      <p className="footer-copy">Built with React · Deployed on Vercel · © 2026 Sinalo Ntlanganiso</p>
    </ScrollReveal>
  </footer>
);

// ─── Navbar ───────────────────────────────────────────────────────────────────
const Portfolio = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <Router>
      <div className="App">
        <nav className={`portfolio-navbar ${scrolled ? 'nav-scrolled' : ''}`}>
          <span className="nav-brand">SA/NTLANGANISO</span>
          <ul className="nav-links">
            <li><NavLink to="/" className={({ isActive }) => isActive ? 'active-link' : ''}><FiHome /> Home</NavLink></li>
            <li><NavLink to="/projects" className={({ isActive }) => isActive ? 'active-link' : ''}><FiActivity /> Projects</NavLink></li>
            <li><NavLink to="/documents" className={({ isActive }) => isActive ? 'active-link' : ''}><FiFileText /> Documents</NavLink></li>
          </ul>
        </nav>

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/documents" element={<Documents />} />
        </Routes>

        <SpotifyPlayer />
      </div>
    </Router>
  );
};

// ─── Skills Data ──────────────────────────────────────────────────────────────
const frontendBackendSkills = [
  { image: 'images/java_3291669.png', name: 'Java' },
  { image: 'images/spingboot.png', name: 'Spring Boot' },
  { image: 'images/jwt.png', name: 'JWT' },
  { image: 'images/html.png', name: 'HTML' },
  { image: 'images/css.png', name: 'CSS' },
  { image: 'images/javascript.png', name: 'JavaScript' },
  { image: 'images/rj.png', name: 'ReactJS' },
  { image: 'images/nodejs.png', name: 'Node.js' },
  { image: 'images/jsonn.png', name: 'JSON' },
  { image: 'images/neet.png', name: '.NET' },
  { image: 'images/o-A.png', name: 'OAuth' },
  { image: 'images/c-plus-plus.png', name: 'C++' },
  { image: 'images/csharp.png', name: 'C#' },
];
const distributionSkills = [
  { image: 'images/sql.png', name: 'MySQL' },
  { image: 'images/mongoDB.png', name: 'MongoDB' },
  { image: 'images/Postgresql.png', name: 'PostgreSQL' },
];
const mobileSkills = [
  { image: 'images/kortlin.png', name: 'Kotlin' },
  { image: 'images/flutter.jpg', name: 'Flutter' },
  { image: 'images/reactnative.png', name: 'React Native' },
];
const deploymentTech = [
  { image: 'images/githuuub.png', name: 'GitHub' },
  { image: 'images/linux.png', name: 'Linux' },
  { image: 'images/docker.png', name: 'Docker' },
  { image: 'images/andriod-studio.webp', name: 'Android Studio' },
  { image: 'images/postman.png', name: 'Postman' },
  { image: 'images/firebase.png', name: 'Firebase' },
];

export default Portfolio;